import { createStore } from 'vuex'
import { auth, db, rtdb } from '@/config/firebase'
import { ref as dbRef, get, child } from 'firebase/database'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  orderBy,
  startAt,
  endAt,
  updateDoc,
  onSnapshot,
  doc,
  getDoc,
  setDoc
} from 'firebase/firestore'


const prefix = 'whatsapp:'

const PHONE_PREFIX_CONFIG = {
  '57': { isWhatsApp: true,  language: 'es' }, // Colombia
  '52': { isWhatsApp: true,  language: 'es' }, // Mexico
  '34': { isWhatsApp: true,  language: 'es' }, // Spain
  '1':  { isWhatsApp: false, language: 'en' }, // USA / Canada
  '44': { isWhatsApp: false, language: 'en' }, // UK
}

function getPhonePrefixMeta(phone) {
  const sorted = Object.entries(PHONE_PREFIX_CONFIG).sort((a, b) => b[0].length - a[0].length)
  for (const [code, meta] of sorted) {
    if (phone.startsWith(code)) return meta
  }
  return { isWhatsApp: false, language: 'en' }
}

export default createStore({
  state: {
    user: null,
    users: [], // Auth users (store owners/staff) 
    customers: [], // Pet owners (business clients)
    interactions: [],
    loading: false,
    error: null,
    unsubscribeListeners: {
      interactions: null,
      users: null,
      customers: null,
      singleInteraction: null
    },
    activeInteraction: null,
    language: 'en', // Default language
    translations: {}, // Translations object
    credits: 0, // credits value for the current user
    demo: null // account status: null (undefined), 1 (demo), 0 (authorized)
  },
  mutations: {
    setUser(state, user) {
      state.user = user
    },
    setUsers(state, users) {
      state.users = users
    },
    setCustomers(state, customers) {
      state.customers = customers
    },
    setInteractions(state, interactions) {
      state.interactions = interactions
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setUnsubscribeListener(state, { type, listener }) {
      state.unsubscribeListeners[type] = listener
    },
    setActiveInteraction(state, interaction) {
      state.activeInteraction = interaction
    },
    setLanguage(state, language) {
      state.language = language
    },
    setTranslations(state, translations) {
      state.translations = translations
    },
    setCredits(state, credits) {
      state.credits = credits
    },
    decrementCredits(state, amount = 1) {
      // decrement if credits are available (demo or normal mode)
      if (state.credits > 0) {
        state.credits = Math.max(0, state.credits - amount)
      }
    },
    setDemoStatus(state, demoStatus) {
      state.demo = demoStatus
    },
  },
  actions: {
    // ... (rest of the code remains the same)
    // Auth actions
    async loginUser({ commit, dispatch }, { email, password }) {
      try {
        commit('setLoading', true)
        commit('setError', null)
        
        // Sign in with Firebase Auth
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        const user = userCredential.user
        
        // Set user in state
        commit('setUser', user)
        
        // Load user's saved language preference
        const q = query(collection(db, 'users'), where('userId', '==', user.uid))
        const querySnapshot = await getDocs(q)
        
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data()
          
          // If user has a preferred language, load it
          if (userData.preferredLanguage) {
            console.log(`Loading user's preferred language: ${userData.preferredLanguage}`)
            await dispatch('fetchTranslations', userData.preferredLanguage)
          } else {
            // Default to English if no preference is set
            await dispatch('fetchTranslations', 'en')
          }
          
          // Load credits value for the user
          const credits = userData.credits !== undefined ? userData.credits : 0
          commit('setCredits', credits)
          
          // Load account status (1=demo, 0=authorized, null=undefined)
          const demoStatus = userData.demo !== undefined ? userData.demo : null
          commit('setDemoStatus', demoStatus)
        }
        
        // Check if all required profile information exists
        let hasCompleteProfile = false
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data()
          hasCompleteProfile = userData.storeName && 
                              userData.phoneNumber && 
                              userData.storeName.trim() !== '' && 
                              userData.phoneNumber.trim() !== ''
        }
        
        return { 
          user, 
          isExistingUser: !querySnapshot.empty,
          hasCompleteProfile
        }
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async createUserWithEmailPassword({ commit }, { email, password }) {
      try {
        commit('setLoading', true)
        commit('setError', null)
        
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, email, password)
        const user = userCredential.user
        
        // We don't set the user in state yet since profile is incomplete
        
        return {
          success: true,
          user
        }
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async registerUser({ commit, dispatch }, { email, password, storeName, phoneNumber }) {
      try {
        commit('setLoading', true)
        commit('setError', null)
        
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, email, password)
        const user = userCredential.user
        
        // language selection from local storage
        const preferredLanguage = localStorage.getItem("selectedCountry") === "co" ? "es" : "en"
        
        // Create user document in Firestore with document ID = userId
        const userRef = doc(db, 'users', user.uid)
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email,
          storeName: storeName,
          phoneNumber: phoneNumber,
          preferredLanguage: preferredLanguage,
          languageSetAt: new Date().toISOString(),
          role: 'owner',
          createdAt: new Date().toISOString()
        })
        
        // Set user in state
        commit('setUser', user)
        
        // Load the selected language
        await dispatch('fetchTranslations', preferredLanguage)
        
        return {
          success: true,
          userId: user.uid
        }
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async registerGoogleUser({ commit, dispatch }, { uid, email, storeName, phoneNumber }) {
      try {
        commit('setLoading', true)
        commit('setError', null)

        // language selection from local storage
        const userLanguage = localStorage.getItem('selectedCountry') === 'co' ? 'es' : 'en';
        
        // Create user document in Firestore with document ID = userId
        const userRef = doc(db, 'users', uid)
        await setDoc(userRef, {
          userId: uid,
          email: email,
          storeName: storeName,
          phoneNumber: phoneNumber,
          preferredLanguage: userLanguage,
          languageSetAt: new Date().toISOString(),
          createdAt: new Date(),
          provider: 'google'
        })
        
        // Get the user data from Firebase Auth
        const user = auth.currentUser
        commit('setUser', user)
        
        // Set the user's preferred language
        await dispatch('fetchTranslations', userLanguage)
        
        return { success: true }
      } catch (error) {
        console.error('Error registering Google user:', error)
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async registerUserDetails({ commit, dispatch }, { storeName, phoneNumber }) {
      try {
        commit('setLoading', true)
        commit('setError', null)
        
        // Get current user from auth
        const user = auth.currentUser
        
        if (!user) {
          throw new Error('No authenticated user found. Please login again.')
        }
        
        // language selection from local storage
        const preferredLanguage = localStorage.getItem("selectedCountry") === "co" ? "es" : "en"
        
        // Create user document in Firestore with document ID = userId
        const userRef = doc(db, 'users', user.uid)
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email,
          storeName: storeName,
          phoneNumber: phoneNumber,
          preferredLanguage: preferredLanguage,
          languageSetAt: new Date().toISOString(),
          demo: false,
          credits: 5,
          role: 'owner',
          createdAt: new Date().toISOString()
        })
        
        // Now set user in state since profile is complete
        commit('setUser', user)
        
        // Load the selected language
        await dispatch('fetchTranslations', preferredLanguage)
        
        return {
          success: true,
          userId: user.uid
        }
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async logout({ commit, dispatch }) {
      try {
        // Clean up all listeners first
        await dispatch('clearAllListeners')
        
        // Then log out and clear data
        await signOut(auth)
        commit('setUser', null)
        commit('setUsers', [])
        commit('setCustomers', [])
        commit('setInteractions', [])
        commit('setActiveInteraction', null)
      } catch (error) {
        commit('setError', error.message)
        throw error
      }
    },

    async changePassword({ commit }, { currentPassword, newPassword }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const user = auth.currentUser
        const credential = EmailAuthProvider.credential(user.email, currentPassword)
        await reauthenticateWithCredential(user, credential)
        await updatePassword(user, newPassword)
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async loginWithGoogle({ commit, dispatch }) {
      try {
        console.log('Login with Google started')
        commit('setLoading', true)
        commit('setError', null)
        
        // Use provider with proper popup configuration for better mobile support
        const provider = new GoogleAuthProvider()
        provider.setCustomParameters({
          // Force account selection even if there's only one account
          // This helps ensure the flow is consistent across devices
          prompt: 'select_account'
        })
        
        // Use signInWithPopup for authentication
        const result = await signInWithPopup(auth, provider)
        const user = result.user
        console.log('Google auth success, user:', user.uid)
        
        // Check if this user already exists in the users collection
        const q = query(collection(db, 'users'), where('userId', '==', user.uid))
        const querySnapshot = await getDocs(q)
        console.log('User record exists:', !querySnapshot.empty)
        
        // Only set the user in state if they already have a complete profile
        let hasCompleteProfile = false
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data()
          // Check if user has already provided store name and phone number
          hasCompleteProfile = userData.storeName && userData.phoneNumber && 
              userData.storeName.trim() !== '' && 
              userData.phoneNumber.trim() !== ''
          
          if (hasCompleteProfile) {
            commit('setUser', user)
            
            // Load user's saved language preference
            if (userData.preferredLanguage) {
              await dispatch('fetchTranslations', userData.preferredLanguage)
            } else {
              // Default to English if no preference is set
              await dispatch('fetchTranslations', 'en')
            }
            
            // upload credits user
            const credits = userData.credits !== undefined ? userData.credits : 0
            commit('setCredits', credits)
            
            // upload demo status (1=demo, 0=authorized, null=no define)
            const demoStatus = userData.demo !== undefined ? userData.demo : null
            commit('setDemoStatus', demoStatus)
          }
        }
        
        return { 
          user, 
          isExistingUser: !querySnapshot.empty,
          hasCompleteProfile
        }
      } catch (error) {
        console.error('Google login error:', error)
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    
    // User and customer actions
    async fetchUsers({ commit, state }) {
      commit('setLoading', true)
      try {
        if (!state.user) {
          commit('setUsers', [])
          return
        }
        
        const usersRef = collection(db, 'users')
        const q = query(usersRef, where('userId', '==', state.user.uid))
        const querySnapshot = await getDocs(q)
        const users = []
        querySnapshot.forEach((doc) => {
          users.push({ id: doc.id, ...doc.data() })
        })
        commit('setUsers', users)
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    
    async fetchCustomers({ commit, state }) {
      commit('setLoading', true)
      try {
        if (!state.user) {
          commit('setCustomers', [])
          return
        }
        
        const customersRef = collection(db, 'customers')
        const q = query(customersRef, where('userId', '==', state.user.uid))
        const querySnapshot = await getDocs(q)
        const customers = []
        querySnapshot.forEach((doc) => {
          customers.push({ id: doc.id, ...doc.data() })
        })
        commit('setCustomers', customers)
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchInteractions({ commit, state }) {
      commit('setLoading', true)
      try {
        if (!state.user) {
          commit('setInteractions', [])
          return
        }

        // Check if we already have a listener and clean it up if so
        if (state.unsubscribeListeners.interactions) {
          state.unsubscribeListeners.interactions()
        }

        const interactionsRef = collection(db, 'interactions')
        
        try {
          // Try complex query first
          const q = query(
            interactionsRef,
            where('userId', '==', state.user.uid),
            orderBy('date', 'desc')
          )
          
          // Set up real-time listener
          const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const interactions = []
            querySnapshot.forEach((doc) => {
              interactions.push({ id: doc.id, ...doc.data() })
            })
            commit('setInteractions', interactions)
            commit('setLoading', false)
          }, (error) => {
            console.error('Error in interactions listener:', error)
            commit('setError', error.message)
            commit('setLoading', false)
          })
          
          // Store the unsubscribe function
          commit('setUnsubscribeListener', { type: 'interactions', listener: unsubscribe })
          
        } catch (indexError) {
          console.error('Index error in fetchInteractions, falling back to simple query:', indexError)
          
          // Fall back to a simpler query
          const simpleQuery = query(
            interactionsRef,
            where('userId', '==', state.user.uid)
          )
          
          // Set up real-time listener for the simple query
          const unsubscribe = onSnapshot(simpleQuery, (querySnapshot) => {
            let interactions = []
            querySnapshot.forEach((doc) => {
              interactions.push({ id: doc.id, ...doc.data() })
            })
            
            // Sort manually since we can't use orderBy
            interactions = interactions.sort((a, b) => {
              return new Date(b.date) - new Date(a.date)
            })
            
            commit('setInteractions', interactions)
            commit('setLoading', false)
          }, (error) => {
            console.error('Error in interactions listener (simple):', error)
            commit('setError', error.message)
            commit('setLoading', false)
          })
          
          // Store the unsubscribe function
          commit('setUnsubscribeListener', { type: 'interactions', listener: unsubscribe })
        }
      } catch (error) {
        console.error('Error fetching interactions:', error)
        commit('setError', error.message)
        commit('setLoading', false)
      }
    },

    async createInteraction({ commit, dispatch, state }, interactionData) {
      commit('setLoading', true)
      try {
        if (!state.user) {
          throw new Error('User must be authenticated to create interactions')
        }
        
        const userId = state.user.uid
        const customersRef = collection(db, 'customers')
        
        // Fetch user information to get the store name
        let storeName = "";
        try {
          // Get user data from the users collection
          const usersRef = collection(db, 'users')
          const userQuery = query(usersRef, where('userId', '==', userId))
          const userSnapshot = await getDocs(userQuery)
          
          if (!userSnapshot.empty) {
            const userData = userSnapshot.docs[0].data()
            // Use storeName field (not store_name)
            storeName = userData.storeName || ""
          } else {
            // Attempt to get store name from state.users if available
            if (state.users && state.users.length > 0) {
              const currentUser = state.users.find(u => u.userId === userId)
              if (currentUser && currentUser.storeName) {
                storeName = currentUser.storeName
              }
            }
          }
        } catch (userError) {
          console.error('Error fetching user info:', userError)
        }
        
        // Use a simple query for customer lookup to avoid index issues
        const simpleQuery = query(
          customersRef, 
          where('userId', '==', userId)
        )
        const allCustomersSnapshot = await getDocs(simpleQuery)
        
        // Determine if this is a WhatsApp number and its language from the phone prefix
        const phoneMeta = getPhonePrefixMeta(interactionData.phone)
        const isWhatsAppPhone = phoneMeta.isWhatsApp

        // Find matching customer by phone number client-side (format-agnostic)
        const barePhone = interactionData.phone.replace(/\D/g, '')
        const customerDoc = allCustomersSnapshot.docs.find(doc => {
          const customerData = doc.data()
          if (!customerData.phone) return false
          const bare = customerData.phone.toString().replace('whatsapp:', '').replace(/^\+/, '').replace(/\D/g, '')
          return bare === barePhone
        })

        let customerId

        // Determine language and first message from phone prefix
        const phoneLanguage = phoneMeta.language;
        const firstMessage = phoneLanguage === 'es'
          ? `Hola ${interactionData.customerName} y ${interactionData.petName}, ¡esperamos que estén bien! Nos encantaría saber ¿Cómo te fue en tu última visita a ${storeName}? Responde NO si quieres dejar de recibir mensajes o cuéntanos tu opinión, esto nos ayuda a mejorar.`
          : `Hi ${interactionData.customerName} and ${interactionData.petName}!, we hope you're doing well! We'd love to know how your last visit to ${storeName} was? Reply NO if you want to stop receiving messages, or tell us your opinion, this helps us improve.`;

        // Build per-pet document ID: {plainPhone}_{petSlug} so each pet has its own interaction doc
        const plainPhone = interactionData.phone.replace(/\D/g, '')
        const petSlug = (interactionData.petName || '').replace(/[^a-zA-Z0-9]/g, '')
        const docId = petSlug ? `${plainPhone}_${petSlug}` : plainPhone

        // Create or update customer first so we have customerId for the n8n payload
        if (!customerDoc) {
          const newCustomerRef = await addDoc(collection(db, 'customers'), {
            name: interactionData.customerName,
            phone: isWhatsAppPhone ? prefix + interactionData.phone : interactionData.phone,
            pets: interactionData.petName ? [interactionData.petName] : [],
            services: interactionData.service ? [interactionData.service] : [],
            userId: userId,
            accepted: false
          })
          customerId = newCustomerRef.id
        } else {
          customerId = customerDoc.id
          const customerData = customerDoc.data()

          const pets = new Set(customerData.pets || [])
          const services = new Set(customerData.services || [])

          if (interactionData.petName) pets.add(interactionData.petName)
          if (interactionData.service) services.add(interactionData.service)

          await updateDoc(customerDoc.ref, {
            pets: Array.from(pets),
            services: Array.from(services),
            phone: isWhatsAppPhone ? prefix + interactionData.phone : interactionData.phone
          })
        }

        // Trigger n8n webhook — n8n is the sole creator of the interaction document in Firestore
        try {
          console.log('[n8n] NODE_ENV:', process.env.NODE_ENV);
          console.log('[n8n] Reading webhook URL from Firestore...');
          const n8nUrlDoc = await getDoc(doc(db, 'parameters', 'n8nWebhookUrl'));
          console.log('[n8n] Document exists:', n8nUrlDoc.exists());
          if (n8nUrlDoc.exists()) {
            let n8nUrl = n8nUrlDoc.data().value;
            if (process.env.NODE_ENV === 'development') {
              const parsed = new URL(n8nUrl);
              n8nUrl = '/n8n-proxy' + parsed.pathname + parsed.search;
            }
            console.log('[n8n] Sending POST to:', n8nUrl);
            const payload = {
              phone: interactionData.phone,
              document_id: docId,
              customerId,
              customerName: interactionData.customerName,
              petName: interactionData.petName,
              storeName,
              service: interactionData.service || '',
              serviceDateTime: interactionData.serviceDateTime || '',
              userId,
              language: phoneLanguage,
              message: firstMessage
            };
            console.log('[n8n] Payload:', payload);
            const fetchHeaders = { 'Content-Type': 'application/json' };
            const n8nApiKeyDoc = await getDoc(doc(db, 'parameters', 'n8nApiKey'));
            if (n8nApiKeyDoc.exists() && n8nApiKeyDoc.data().value) {
              fetchHeaders['x-api-key'] = n8nApiKeyDoc.data().value;
            }
            const n8nResponse = await fetch(n8nUrl, {
              method: 'POST',
              headers: fetchHeaders,
              body: JSON.stringify(payload)
            });
            console.log('[n8n] Response status:', n8nResponse.status);
          } else {
            console.warn('[n8n] n8nWebhookUrl not found in Firestore parameters — skipping outbound message');
          }
        } catch (n8nError) {
          console.error('[n8n] Error triggering webhook:', n8nError);
        }
        
        // decrement credits (demo or normal mode)
        if (state.credits > 0) {
          commit('decrementCredits')
          
          // Update credits in Firestore
          try {
            const userDocRef = query(collection(db, 'users'), where('userId', '==', userId))
            const userQuerySnapshot = await getDocs(userDocRef)
            
            if (!userQuerySnapshot.empty) {
              const userDoc = userQuerySnapshot.docs[0].ref
              const userData = userQuerySnapshot.docs[0].data()
              const newCredits = Math.max(0, (userData.credits || 0) - 1)
              
              await updateDoc(userDoc, { credits: newCredits })
            }
          } catch (creditsError) {
            console.error('Error updating credits in Firestore:', creditsError)
          }
        }
        
        // Refresh data
        await dispatch('fetchUsers')
        await dispatch('fetchInteractions')
      } catch (error) {
        commit('setError', error.message)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchSingleInteraction({ commit, state }, interactionId) {
      try {
        if (!state.user || !interactionId) {
          commit('setActiveInteraction', null)
          return
        }
        
        // Clean up previous listener if exists
        if (state.unsubscribeListeners.singleInteraction) {
          state.unsubscribeListeners.singleInteraction()
        }
        
        // Set up real-time listener for the specific interaction
        const interactionRef = doc(db, 'interactions', interactionId)
        
        const unsubscribe = onSnapshot(interactionRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const interactionData = { id: docSnapshot.id, ...docSnapshot.data() }
            commit('setActiveInteraction', interactionData)
          } else {
            console.error('Interaction not found')
            commit('setActiveInteraction', null)
          }
        }, (error) => {
          console.error('Error in single interaction listener:', error)
          commit('setError', error.message)
        })
        
        // Store the unsubscribe function
        commit('setUnsubscribeListener', { type: 'singleInteraction', listener: unsubscribe })
        
        // Immediately fetch the interaction once
        const docSnap = await getDoc(interactionRef)
        if (docSnap.exists()) {
          commit('setActiveInteraction', { id: docSnap.id, ...docSnap.data() })
        } else {
          console.error('Interaction not found')
          commit('setActiveInteraction', null)
        }
      } catch (error) {
        console.error('Error fetching single interaction:', error)
        commit('setError', error.message)
      }
    },

    async searchCustomersByPhone({ commit, state }, phone) {
      try {
        if (!state.user) return []
        
        const customersRef = collection(db, 'customers')
        
        // Derive Firestore phone format from the prefix already baked into `phone`
        // WhatsApp customers are stored as whatsapp:XXXXXXXXXXX
        const searchValue = getPhonePrefixMeta(phone).isWhatsApp
          ? `whatsapp:${phone}`
          : phone

        try {
          // Try the complex query first (requires composite index)
          const q = query(
            customersRef,
            where('userId', '==', state.user.uid),
            orderBy('phone'),
            startAt(searchValue),
            endAt(searchValue + '\uf8ff')
          )
          const querySnapshot = await getDocs(q)
          return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        } catch (indexError) {
          console.error('Index error in searchCustomersByPhone, falling back to client-side filtering:', indexError)
          
          // Fall back to a simpler query with client-side filtering
          const simpleQuery = query(
            customersRef,
            where('userId', '==', state.user.uid)
          )
          const querySnapshot = await getDocs(simpleQuery)
          
          // Filter and sort on the client side
          return querySnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(customer => 
              customer.phone && 
              customer.phone.toString().includes(phone.toString())
            )
            .sort((a, b) => a.phone.localeCompare(b.phone))
        }
      } catch (error) {
        commit('setError', error.message)
        console.error('Error in searchCustomersByPhone:', error)
        return []
      }
    },

    async searchCustomersByName({ commit, state }, name) {
      try {
        if (!state.user) return []
        
        const customersRef = collection(db, 'customers')
        
        try {
          // Try the complex query first (requires composite index)
          const q = query(
            customersRef,
            where('userId', '==', state.user.uid),
            orderBy('name'),
            startAt(name),
            endAt(name + '\uf8ff')
          )
          const querySnapshot = await getDocs(q)
          return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        } catch (indexError) {
          console.error('Index error in searchCustomersByName, falling back to client-side filtering:', indexError)
          
          // Fall back to a simpler query with client-side filtering
          const simpleQuery = query(
            customersRef,
            where('userId', '==', state.user.uid)
          )
          const querySnapshot = await getDocs(simpleQuery)
          
          // Filter and sort on the client side
          return querySnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(customer => 
              customer.name && 
              customer.name.toLowerCase().includes(name.toLowerCase())
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        }
      } catch (error) {
        commit('setError', error.message)
        console.error('Error in searchCustomersByName:', error)
        return []
      }
    },

    async searchPetsByName({ commit, state }, name) {
      try {
        if (!state.user) return []
        
        const customersRef = collection(db, 'customers')
        const q = query(customersRef, where('userId', '==', state.user.uid))
        const querySnapshot = await getDocs(q)
        const allPets = new Set()
        querySnapshot.forEach(doc => {
          const pets = doc.data().pets || []
          pets.forEach(pet => {
            if (pet.toLowerCase().includes(name.toLowerCase())) {
              allPets.add(pet)
            }
          })
        })
        return Array.from(allPets)
      } catch (error) {
        commit('setError', error.message)
        return []
      }
    },

    async searchServices({ commit, state }, service) {
      try {
        if (!state.user) return []
        
        const customersRef = collection(db, 'customers')
        const q = query(customersRef, where('userId', '==', state.user.uid))
        const querySnapshot = await getDocs(q)
        const allServices = new Set()
        querySnapshot.forEach(doc => {
          const services = doc.data().services || []
          services.forEach(s => {
            if (s.toLowerCase().includes(service.toLowerCase())) {
              allServices.add(s)
            }
          })
        })
        return Array.from(allServices)
      } catch (error) {
        commit('setError', error.message)
        return []
      }
    },
    clearActiveInteraction({ commit, state }) {
      if (state.unsubscribeListeners.singleInteraction) {
        state.unsubscribeListeners.singleInteraction()
        commit('setUnsubscribeListener', { type: 'singleInteraction', listener: null })
      }
      commit('setActiveInteraction', null)
    },
    clearAllListeners({ commit, state }) {
      // Unsubscribe from all active listeners
      Object.entries(state.unsubscribeListeners).forEach(([key, unsubscribe]) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe()
          commit('setUnsubscribeListener', { type: key, listener: null })
        }
      })
    },
    async updateInteraction({ commit, state }, { interactionId, data }) {
      try {
        if (!state.user || !interactionId) {
          throw new Error('User must be authenticated and interactionId is required')
        }

        const interactionRef = doc(db, 'interactions', interactionId)
        return updateDoc(interactionRef, data)
          .then(() => {
            // The listener will automatically update the state
            return true
          })
          .catch(error => {
            console.error('Error updating interaction:', error)
            commit('setError', error.message)
            return false
          })
      } catch (error) {
        console.error('Error updating interaction:', error)
        commit('setError', error.message)
        return false
      }
    },
    async sendMessage({ commit, state }, { phone, message, interactionId, documentId }) {
      console.log('[sendMessage] ▶ Called with:', { phone, interactionId, documentId, messageLength: message?.length })
      try {
        if (!state.user) throw new Error('User must be authenticated to send messages')
        if (!phone || !message) throw new Error('Phone and message are required')
        if (!interactionId) throw new Error('interactionId is required')

        // Normalize phone — strip everything except digits
        const normalizedPhone = (phone + '').replace(/\D/g, '')
        console.log('[sendMessage] 📞 Normalized phone:', normalizedPhone)

        // ── Step 1: Read current Firestore doc ──────────────────────────────
        const interactionRef = doc(db, 'interactions', interactionId)
        console.log('[sendMessage] 🔍 Reading Firestore doc:', interactionId)
        const interactionDoc = await getDoc(interactionRef)

        if (!interactionDoc.exists()) {
          throw new Error(`Firestore interaction not found: ${interactionId}`)
        }

        const interactionData = interactionDoc.data()
        const currentConversation = interactionData.conversation || []
        console.log('[sendMessage] 💬 Current conversation length:', currentConversation.length)

        // ── Step 2: Append agent message & write back to Firestore ──────────
        const messageTime = new Date().toISOString()
        const newEntry = { role: 'assistant', message, time: messageTime }
        const updatedConversation = [...currentConversation, newEntry]

        console.log('[sendMessage] ✏️  Writing updated conversation to Firestore...')
        await updateDoc(interactionRef, {
          conversation: updatedConversation,
          lastAgentMessageAt: messageTime,
          lastMessageDate: Date.now()
        })
        console.log('[sendMessage] ✅ Firestore updated — conversation now has', updatedConversation.length, 'entries')

        // ── Step 3: Call n8n agent-message webhook → Twilio ─────────────────
        try {
          const n8nUrlDoc = await getDoc(doc(db, 'parameters', 'n8nWebhookUrl'))
          if (!n8nUrlDoc.exists()) {
            console.warn('[sendMessage] ⚠️  n8nWebhookUrl not found in Firestore — Twilio send skipped')
          } else {
            const existingUrl = n8nUrlDoc.data().value
            const parsed = new URL(existingUrl)
            const baseUrl = `${parsed.protocol}//${parsed.host}`
            let n8nUrl = process.env.NODE_ENV === 'development'
              ? `/n8n-proxy/webhook/agent-message-webhook`
              : `${baseUrl}/webhook/agent-message-webhook`

            const payload = {
              phone: normalizedPhone,
              message,
              document_id: documentId || interactionId,
              isAgentMessage: true,
              userId: state.user.uid
            }

            const fetchHeaders = { 'Content-Type': 'application/json' }
            const n8nApiKeyDoc = await getDoc(doc(db, 'parameters', 'n8nApiKey'))
            if (n8nApiKeyDoc.exists() && n8nApiKeyDoc.data().value) {
              fetchHeaders['x-api-key'] = n8nApiKeyDoc.data().value
            }

            console.log('[sendMessage] 🚀 POST', n8nUrl)
            console.log('[sendMessage] 📦 Payload:', JSON.stringify(payload))

            const res = await fetch(n8nUrl, { method: 'POST', headers: fetchHeaders, body: JSON.stringify(payload) })
            let resBody = ''
            try { resBody = await res.text() } catch { resBody = '(unreadable)' }

            if (!res.ok) {
              console.error(`[sendMessage] ❌ n8n responded ${res.status}:`, resBody)
              throw new Error(`n8n webhook returned ${res.status}: ${resBody}`)
            }

            console.log(`[sendMessage] ✅ n8n responded ${res.status}:`, resBody)
          }
        } catch (n8nError) {
          // n8n failure: Firestore is already updated, re-throw so UI can show the error
          console.error('[sendMessage] ❌ n8n/Twilio error:', n8nError)
          throw n8nError
        }

        console.log('[sendMessage] 🎉 Done')
        return true
      } catch (error) {
        console.error('[sendMessage] 💥 Fatal error:', error)
        commit('setError', error.message)
        throw error
      }
    },
    async fetchTranslations({ commit }, lang) {
      try {
        console.log(`Fetching translations for ${lang} from Firebase`)
        const dbReference = dbRef(rtdb)
        const snapshot = await get(child(dbReference, `copies_${lang}`))
        
        if (snapshot.exists()) {
          const data = snapshot.val()
          if (data && typeof data === 'object') {
            console.log(`Successfully loaded translations for ${lang}`)
            commit('setTranslations', data)
            commit('setLanguage', lang)
            return true
          } else {
            console.error(`Invalid translation data structure for ${lang}`)
            commit('setError', `Invalid translation data structure for ${lang}`)
            return false
          }
        } else {
          console.error(`No translations found for ${lang}`)
          commit('setError', `No translations found for ${lang}`)
          return false
        }
      } catch (error) {
        console.error(`Error fetching translations for ${lang}:`, error)
        commit('setError', error.message)
        return false
      }
    },
    async setUserLanguage({ commit }, { userId, language }) {
      try {
        if (!userId || !['en', 'es'].includes(language)) {
          throw new Error('Invalid user ID or language selection')
        }

        // Update the user document in Firestore
        const userRef = doc(db, 'users', userId)
        await updateDoc(userRef, { 
          preferredLanguage: language,
          languageSetAt: new Date().toISOString()
        })

        // Also update current app language
        commit('setLanguage', language)
        console.log(`User language preference set to ${language}`)
        return true
      } catch (error) {
        console.error('Error setting user language:', error)
        commit('setError', error.message)
        return false
      }
    },
  },
  getters: {
    isAuthenticated: state => !!state.user,
    currentUser: state => state.user,
    getUserById: state => id => state.users.find(user => user.id === id),
    getInteractionsByCustomerId: state => customerId =>
      state.interactions.filter(interaction => interaction.customerId === customerId),
    currentLanguage: state => state.language,
    translate: state => (key, fallback) => {
      // If no translations are loaded
      if (!state.translations || Object.keys(state.translations).length === 0) {
        return fallback;
      }
      
      // Handle keys with section prefixes like "dashboard.title"
      if (key.includes('.')) {
        const [section, subKey] = key.split('.');
        if (state.translations[section] && state.translations[section][subKey] !== undefined) {
          return state.translations[section][subKey];
        }
      } 
      // For legacy keys without section prefix - try to find in each section
      else {
        for (const section of ['app', 'auth', 'customers', 'dashboard', 'interactions', 'messages', 'settings', 'whatsApp']) {
          if (state.translations[section] && state.translations[section][key] !== undefined) {
            return state.translations[section][key];
          }
        }
      }
      
      return fallback;
    }
  }
})
